import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import "./MyServices.css"; // Import isolated CSS

const MyServices = () => {
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [formErrors, setFormErrors] = useState({});

  const { bearerAuth } = useApi();
  const { user, getToken } = useAuth();

  // Form state - Only fields present in API
  const [formData, setFormData] = useState({
    id: "",
    service_name: "",
    services_category: "",
    description: "",
    price: "",
    duration: "",
    discount: "",
    available_days: "",
    available_time_start: "",
    available_time_end: "",
    status: "Active",
  });

  const categories = [
    "Cleaning",
    "Repair",
    "Painting",
    "Maintenance",
    "Home Services",
    "Automotive",
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  // ✅ Fetch all services
  const fetchServices = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await bearerAuth(`vendor/service/list`, "GET", null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status) {
        setServices(response.data.data || []);
      } else {
        showAlert("Failed to fetch services", "danger");
      }
    } catch (err) {
      console.error(err);
      showAlert("Error fetching services", "danger");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 4000);
  };

  // ✅ Validation - Only for required API fields
  const validateForm = () => {
    const errors = {};
    if (!formData.service_name.trim())
      errors.service_name = "Service name is required";
    if (!formData.services_category)
      errors.services_category = "Category is required";
    if (!formData.description.trim())
      errors.description = "Description is required";
    if (!formData.price || formData.price <= 0)
      errors.price = "Valid price is required";
    if (!formData.duration.trim()) errors.duration = "Duration is required";
    if (!formData.available_days.trim())
      errors.available_days = "Available days are required";
    if (!formData.available_time_start)
      errors.available_time_start = "Start time is required";
    if (!formData.available_time_end)
      errors.available_time_end = "End time is required";

    if (
      formData.available_time_start &&
      formData.available_time_end &&
      formData.available_time_start >= formData.available_time_end
    ) {
      errors.available_time_end = "End time must be after start time";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      service_name: "",
      services_category: "",
      description: "",
      price: "",
      duration: "",
      discount: "",
      available_days: "",
      available_time_start: "",
      available_time_end: "",
      status: "Active",
    });
    setFormErrors({});
  };

  const handleAddNew = () => {
    setEditMode(false);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (service) => {
    setEditMode(true);
    setFormData({
      id: service.id,
      service_name: service.service_name || "",
      services_category: service.services_category || "",
      description: service.description || "",
      price: service.price || "",
      duration: service.duration || "",
      discount: service.discount || "",
      available_days: service.available_days || "",
      available_time_start: service.available_time_start || "",
      available_time_end: service.available_time_end || "",
      status: service.status === "1" ? "Active" : "Inactive",
    });
    setShowModal(true);
  };

  // ✅ Add / Update Service
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showAlert("Please fill all required fields", "danger");
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      
      const requestData = {
        vendor_id: user?.id || 1,
        service_name: formData.service_name,
        services_category: formData.services_category,
        description: formData.description,
        price: formData.price,
        duration: formData.duration,
        discount: formData.discount || 0,
        available_days: formData.available_days,
        available_time_start: formData.available_time_start,
        available_time_end: formData.available_time_end,
        status: formData.status === "Active" ? "1" : "0",
      };

      let response;
      if (editMode) {
        requestData.id = formData.id;
        response = await bearerAuth(`vendor/service/update`, "POST", requestData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        response = await bearerAuth(`vendor/service/add`, "POST", requestData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.data?.status) {
        showAlert(
          `Service ${editMode ? "updated" : "added"} successfully!`,
          "success"
        );
        setShowModal(false);
        resetForm();
        fetchServices();
      } else {
        showAlert("Failed to save service", "danger");
      }
    } catch (err) {
      console.error(err);
      showAlert("Error saving service", "danger");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete
  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    try {
      setLoading(true);
      const token = getToken();
      const response = await bearerAuth(
        `vendor/service/delete`,
        "POST",
        { id: serviceId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status) {
        showAlert("Service deleted successfully!", "success");
        fetchServices();
      } else {
        showAlert("Failed to delete service", "danger");
      }
    } catch (err) {
      console.error(err);
      showAlert("Error deleting service", "danger");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Toggle status
  const handleToggleStatus = async (service) => {
    try {
      setLoading(true);
      const token = getToken();
      const newStatus = service.status === "1" ? "0" : "1";

      const requestData = {
        id: service.id,
        vendor_id: user?.id || 1,
        status: newStatus,
      };

      const response = await bearerAuth(`vendor/service/update`, "POST", requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.status) {
        showAlert(
          `Service ${newStatus === "1" ? "activated" : "deactivated"} successfully!`,
          "success"
        );
        fetchServices();
      } else {
        showAlert("Failed to update status", "danger");
      }
    } catch (err) {
      console.error(err);
      showAlert("Error updating status", "danger");
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    return status == "1" 
      ? { text: "Active", variant: "success" }
      : { text: "Inactive", variant: "secondary" };
  };

  // Responsive card view for mobile
  const renderMobileServiceCard = (service, index) => {
    const s = getStatusInfo(service.status);
    return (
      <div key={service.id} className="my-services-mobile-card">
        <div className="my-services-card-header">
          <h4>{service.service_name}</h4>
          <Badge bg={s.variant}>{s.text}</Badge>
        </div>
        <div className="my-services-card-body">
          <div className="my-services-info-row">
            <span className="my-services-label">Category:</span>
            <span>{service.services_category}</span>
          </div>
          <div className="my-services-info-row">
            <span className="my-services-label">Price:</span>
            <span>₹{service.price}</span>
          </div>
          <div className="my-services-info-row">
            <span className="my-services-label">Duration:</span>
            <span>{service.duration}</span>
          </div>
        </div>
        <div className="my-services-card-actions">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleEdit(service)}
            className="my-services-action-btn"
          >
            <i className="fas fa-edit"></i> Edit
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDelete(service.id)}
            className="my-services-action-btn"
          >
            <i className="fas fa-trash"></i> Delete
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="my-services-container">
      <div className="my-services-header">
        <h3>My Services</h3>
        <Button variant="primary" onClick={handleAddNew} className="my-services-add-btn">
          <i className="fas fa-plus me-2"></i> Add New Service
        </Button>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false, message: "", type: "" })}
          className="my-services-alert"
        >
          {alert.message}
        </Alert>
      )}

      <Card className="my-services-card">
        <Card.Body>
          {loading && services.length === 0 ? (
            <div className="my-services-loading">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="my-services-table-view">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.length > 0 ? (
                      services.map((service, i) => {
                        const s = getStatusInfo(service.status);
                        return (
                          <tr key={service.id}>
                            <td>{i + 1}</td>
                            <td>{service.service_name}</td>
                            <td>{service.services_category}</td>
                            <td>₹{service.price}</td>
                            <td>{service.duration}</td>
                            <td>
                              <Badge bg={s.variant}>{s.text}</Badge>
                            </td>
                            <td>
                              <div className="my-services-actions">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleEdit(service)}
                                  className="my-services-action-icon"
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(service.id)}
                                  className="my-services-action-icon"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="my-services-empty">
                          No services found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="my-services-mobile-view">
                {services.length > 0 ? (
                  services.map((service, i) => renderMobileServiceCard(service, i))
                ) : (
                  <div className="my-services-empty-card">
                    No services found.
                  </div>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Form - Only API fields */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        size="lg"
        dialogClassName="my-services-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "Edit Service" : "Add New Service"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Service Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="service_name"
                    value={formData.service_name}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.service_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.service_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    name="services_category"
                    value={formData.services_category}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.services_category}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.services_category}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Price (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.price}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.price}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Duration *</Form.Label>
                  <Form.Control
                    type="text"
                    name="duration"
                    placeholder="e.g., 2 hours, 30 mins"
                    value={formData.duration}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.duration}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.duration}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Discount (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Available Days *</Form.Label>
                  <Form.Control
                    type="text"
                    name="available_days"
                    placeholder="e.g., Monday-Friday, Weekends"
                    value={formData.available_days}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.available_days}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.available_days}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Time *</Form.Label>
                  <Form.Control
                    type="time"
                    name="available_time_start"
                    value={formData.available_time_start}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.available_time_start}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.available_time_start}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Time *</Form.Label>
                  <Form.Control
                    type="time"
                    name="available_time_end"
                    value={formData.available_time_end}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.available_time_end}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.available_time_end}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    placeholder="Describe your service in detail..."
                    value={formData.description}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.description}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.description}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : editMode ? (
                "Update Service"
              ) : (
                "Add Service"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MyServices;